// middleware/abortMiddleware.js

import { OBJECTS_TYPE } from '../reducers/objectReducers/type';

const abortMiddleware = (store) => (next) => (action) => {
  // Check if this action should abort other actions
  const aborted = abortActions(action.type);

  // Add abort capability to actions that need it
  if (action.meta?.shouldAbort || abortConfig[action.type]) {
    const controller = getAbortController(action.type);
    action.meta = {
      ...action.meta,
      abortSignal: controller.signal,
      abortController: controller,
      canAbort: true,
    };

    console.log(`[AbortMiddleware] Added AbortController to ${action.type}`);
  }

  // Add aborted actions info to action for debugging
  if (aborted.length > 0) {
    action.meta = {
      ...action.meta,
      abortedActions: aborted,
    };
  }

  return next(action);
};

export default abortMiddleware;

export const abortConfig = {
  // When user saves, cancel any pending reads (data will be stale)
  [OBJECTS_TYPE.SAVE_OBJECT]: {
    aborts: [
      OBJECTS_TYPE.REQUEST_TREE_BY_PATTERN, // Tree view will be out of date
      OBJECTS_TYPE.SELECT_OBJECT, // Object details will be out of date
    ],
    reason: 'save-operation-invalidates-reads',
  },

  [OBJECTS_TYPE.SAVE_OBJECTFORMDATA]: {
    aborts: [
      OBJECTS_TYPE.REQUEST_TREE_BY_PATTERN, // Tree view will be out of date
      OBJECTS_TYPE.SELECT_OBJECT, // Object details will be out of date
    ],
    reason: 'save-operation-invalidates-reads',
  },

  // When user navigates to new object, cancel old object fetch
  [OBJECTS_TYPE.SELECT_OBJECT]: {
    aborts: [
      OBJECTS_TYPE.SELECT_OBJECT, // Previous object no longer needed
    ],
    reason: 'navigation-obsoletes-previous-request',
  },

  // When user searches, previous search is obsolete
  [OBJECTS_TYPE.SEARCH]: {
    aborts: [OBJECTS_TYPE.SEARCH],
    reason: 'newer-search-obsoletes-previous',
  },

  // When user changes tree pattern, previous tree is obsolete
  [OBJECTS_TYPE.REQUEST_TREE_BY_PATTERN]: {
    aborts: [OBJECTS_TYPE.REQUEST_TREE_BY_PATTERN],
    reason: 'newer-tree-request-obsoletes-previous',
  },
};

const abortControllers = new Map();

export const getAbortController = (actionType) => {
  const key = `${actionType}_${Date.now()}`;
  const controller = new AbortController();

  // Store with metadata for tracking
  abortControllers.set(actionType, {
    controller,
    key,
    createdAt: Date.now(),
  });

  return controller;
};

export const abortActions = (actionType, reason = 'unknown') => {
  const config = abortConfig[actionType];
  if (!config) return [];

  const abortedActions = [];
  const effectiveReason = config.reason || reason;
  config.aborts.forEach((typeToAbort) => {
    const entry = abortControllers.get(typeToAbort);
    if (entry && entry.controller) {
      console.log(
        `[AbortMiddleware] Aborting ${typeToAbort} (reason: ${effectiveReason})`
      );
      entry.controller.abort(
        new Error(`Aborted by ${actionType}: ${effectiveReason}`)
      );
      abortedActions.push({
        type: typeToAbort,
        abortedAt: Date.now(),
        reason: effectiveReason,
      });
      abortControllers.delete(typeToAbort);
    }
  });

  return abortedActions;
};
